/* ============================================
   Portal Facturas — Financial Center
   Structured table, chip filters, slide-over detail,
   Flow.cl pay button, auto-vencida detection
   ============================================ */

import React, { useState, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { descargarPdf } from '@/lib/documentos'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useCachedData } from '@/hooks/useCachedData'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { showToast } from '@/components/layout/Toast'
import type { Factura, FacturaComprobante, Empresa } from '@/lib/types'
import { Breadcrumbs } from '@/components/ui'
import { useCrumbInicio } from '@/lib/portal-base'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

const estadoConfig: Record<string, { label: string; className: string; color: string }> = {
  // 'Por emitir' y no 'Pendiente': un borrador solo lo ve el PARTNER (al
  // cliente se le filtra), y para él es lo que nos debe por una venta que
  // acaba de cerrar — todavía sin comprobante, así que no se puede pagar.
  borrador: { label: 'Por emitir', className: 'badge-enviada', color: '#0984e3' },
  pre_factura: { label: 'Pendiente', className: 'badge-enviada', color: '#0984e3' },
  emitida: { label: 'Emitida', className: 'badge-enviada', color: '#0984e3' },
  pago_reportado: { label: 'Pago Reportado', className: 'badge-pago-reportado', color: '#fdcb6e' },
  pagada: { label: 'Pagada', className: 'badge-aceptada', color: '#00b894' },
  anulada: { label: 'Anulada', className: 'badge-rechazada', color: '#d63031' },
}

// Icons
function IconDollar() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
}
function IconCheck() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
}
function IconFile() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
}

// Un mismo registro puede ser una CONSTANCIA interna (sin valor tributario) o un comprobante
// electrónico SUNAT ya emitido. La llave es `sunat_serie`: si existe, es boleta/factura real
// (con serie-correlativo y enlace de consulta). Si no, es solo una constancia de pago.
function infoDocumento(f: Factura): { titulo: string; numero: string; electronico: boolean; enlace: string | null } {
  if (f.sunat_serie && f.sunat_numero != null) {
    const nombre = f.tipo_documento_sunat === '03' ? 'Boleta'
      : f.tipo_documento_sunat === '07' ? 'Nota de crédito'
      : f.tipo_documento_sunat === '08' ? 'Nota de débito'
      : 'Factura'
    return { titulo: nombre, numero: `${f.sunat_serie}-${String(f.sunat_numero).padStart(8, '0')}`, electronico: true, enlace: f.sunat_enlace_consulta }
  }
  return { titulo: 'Constancia de pago', numero: f.numero, electronico: false, enlace: null }
}

export default function PortalFacturas() {
  const { user, isPartner, token } = useAuth()
  const { activeClienteId: clienteId } = usePortalCliente()
  const inicio = useCrumbInicio()
  const [filterEstado, setFilterEstado] = useState<string>('')
  const [uploading, setUploading] = useState<number | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [previewFactura, setPreviewFactura] = useState<Factura | null>(null)
  const [payModalFactura, setPayModalFactura] = useState<Factura | null>(null)
  const [exporting, setExporting] = useState(false)
  const [confirmModal, setConfirmModal] = useState<{ facturaId: number; files: string[] } | null>(null)
  const previewRef = useRef<HTMLDivElement>(null)

  // Las facturas y sus comprobantes, en caché: volver a la pestaña no vuelve a
  // pedirlos. `clienteId` va en la clave porque el multi-cuenta cambia de
  // empresa sin recargar la app.
  const { data: datos, isLoading: loading, actualizar } = useCachedData(
    ['portal-facturas', clienteId, isPartner ? 'con-borradores' : 'emitidas'],
    async () => {
      /*
        El borrador se le oculta al cliente —es un documento nuestro que
        todavía puede cambiar— pero NO al partner: al cerrar una venta como
        ganada se genera acá lo que nos debe, y esconderlo hasta emitirlo
        dejaba su panel diciendo «sin facturas» justo después de que el
        modal le prometiera lo contrario.
      */
      let consulta = supabase
        .from('facturas')
        .select('*')
        .eq('cliente_id', clienteId)
      if (!isPartner) consulta = consulta.neq('estado', 'borrador')
      const { data: fData } = await consulta
        .order('fecha_emision', { ascending: false })
      const facturas = (fData ?? []) as unknown as Factura[]
      if (facturas.length === 0) return { facturas, comprobantes: [] as FacturaComprobante[] }
      const { data: cData } = await supabase
        .from('factura_comprobantes')
        .select('*')
        .in('factura_id', facturas.map(f => f.id))
        .order('created_at', { ascending: false })
      return { facturas, comprobantes: (cData ?? []) as unknown as FacturaComprobante[] }
    },
    { activo: !!clienteId },
  )
  const facturas = datos?.facturas ?? []
  const comprobantes = datos?.comprobantes ?? []

  // Los datos del emisor para el preview. Clave aparte y compartida: es un
  // singleton que no cambia, y así una segunda pantalla que lo pida no repite
  // la consulta.
  // Stopgap: solo columnas públicas (branding/facturación); nunca secretos
  // (google_tokens, canales_config, firma_email_html). Fix real: RLS/vista en DB.
  const { data: empresa = null } = useCachedData(['portal-empresa'], async () => {
    const { data } = await supabase.from('empresa')
      .select('id, nombre, razon_social, ruc, telefono, email, website, ubicacion, logo_url, color_primario, color_secundario, banco, cuenta_corriente, cci, swift, direccion_banco')
      .limit(1).single()
    return (data ?? null) as unknown as Empresa | null
  })

  /*
    El documento lo arma el backend. Si la factura está emitida en SUNAT lo
    que baja es el PDF de NubeFact —el comprobante de verdad—, y si no, el
    detalle de cobro armado con la plantilla.

    Antes esto fotografiaba la pantalla con html2canvas: salía una imagen,
    sin texto que el contador pudiera copiar, y a veces con bloques en
    blanco. Ver `src/lib/documentos.ts`.
  */
  async function exportPDF() {
    if (!previewFactura) return
    setExporting(true)
    try {
      const info = infoDocumento(previewFactura)
      await descargarPdf(
        `${API_URL}/api/pdf/factura/${previewFactura.id}/archivo`,
        `${(info?.titulo ?? 'Comprobante').replace(/\s+/g, '-')}-${info?.numero ?? previewFactura.id}`,
        token,
      )
      showToast('PDF descargado', 'success')
    } catch { showToast('No se pudo descargar el documento', 'error') }
    finally { setExporting(false) }
  }

  async function handleUploadComprobante(facturaId: number, files: FileList) {
    setUploading(facturaId)
    const uploadedNames: string[] = []
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop() || 'pdf'
        const path = `${clienteId}/${facturaId}/${Date.now()}-${Math.random().toString(36).slice(2,6)}.${ext}`
        const { error: uploadErr } = await supabase.storage
          .from('factura-comprobantes')
          .upload(path, file)
        if (uploadErr) throw uploadErr

        const { data: urlData } = supabase.storage
          .from('factura-comprobantes')
          .getPublicUrl(path)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: comp, error: dbErr } = await (supabase
          .from('factura_comprobantes') as any)
          .insert({
            factura_id: facturaId,
            url: urlData.publicUrl,
            nombre: file.name,
            tipo: file.type,
            subido_por: user?.id || null,
          })
          .select()
          .single()
        if (dbErr) throw dbErr

        actualizar(prev => ({ ...prev, comprobantes: [comp as unknown as FacturaComprobante, ...prev.comprobantes] }))
        uploadedNames.push(file.name)
      }
      // Show confirm modal after successful upload
      setConfirmModal({ facturaId, files: uploadedNames })
    } catch (err) {
      console.error('Upload error:', err)
      showToast('Error al subir comprobante', 'error')
    } finally {
      setUploading(null)
    }
  }

  async function handleConfirmPayment(facturaId: number) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from('facturas') as any)
        .update({
          pago_confirmado_cliente: true,
          fecha_confirmacion_cliente: new Date().toISOString(),
        })
        .eq('id', facturaId)
      if (error) throw error

      actualizar(prev => ({
        ...prev,
        facturas: prev.facturas.map(f =>
          f.id === facturaId
            ? { ...f, pago_confirmado_cliente: true, fecha_confirmacion_cliente: new Date().toISOString() }
            : f
        ),
      }))
      setConfirmModal(null)
      showToast('Pago reportado exitosamente. El equipo validará su pago.', 'success')

      // Notify admin (non-blocking)
      fetch(`${API_URL}/api/notifications/payment-reported`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ factura_id: facturaId }),
      }).catch(() => {})
    } catch (err) {
      console.error('Confirm payment error:', err)
      showToast('Error al confirmar pago', 'error')
    }
  }

  async function handleDeleteComprobante(comp: FacturaComprobante) {
    if (!confirm('¿Eliminar este comprobante?')) return
    try {
      // Extract storage path from public URL
      const urlParts = comp.url.split('/factura-comprobantes/')
      const storagePath = urlParts.length > 1 ? decodeURIComponent(urlParts[1]) : null

      // Delete from storage
      if (storagePath) {
        await supabase.storage.from('factura-comprobantes').remove([storagePath])
      }

      // Delete from DB
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from('factura_comprobantes') as any)
        .delete()
        .eq('id', comp.id)
      if (error) throw error

      actualizar(prev => ({ ...prev, comprobantes: prev.comprobantes.filter(c => c.id !== comp.id) }))
      showToast('Comprobante eliminado', 'success')
    } catch (err) {
      console.error('Delete error:', err)
      showToast('Error al eliminar comprobante', 'error')
    }
  }

  // Determine if a factura is overdue
  function isVencida(f: Factura): boolean {
    if (f.estado !== 'emitida' || !f.fecha_vencimiento) return false
    return new Date(f.fecha_vencimiento) < new Date()
  }

  const filtered = filterEstado
    ? facturas.filter(f => f.estado === filterEstado)
    : facturas

  /*
    Lo que debe. Para el partner cuenta tambien el borrador: es la deuda de
    una venta que ya cerro, y dejarla fuera ponia «S/ 0.00 pendiente» arriba
    de una tabla con dos facturas por emitir.
  */
  const estadosPendientes = isPartner
    ? ['emitida', 'pre_factura', 'borrador']
    : ['emitida', 'pre_factura']
  const totalPendiente = facturas
    .filter(f => estadosPendientes.includes(f.estado))
    .reduce((sum, f) => sum + f.total, 0)
  const totalPagado = facturas
    .filter(f => f.estado === 'pagada')
    .reduce((sum, f) => sum + f.total, 0)
  // Moneda real de los comprobantes (evita el default USD → mostraba "5.00 USD" en pagos PEN).
  const monedaCliente = facturas.find(f => f.moneda)?.moneda || 'PEN'


  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        <p>Cargando facturas...</p>
      </div>
    )
  }

  return (
    <div className="pf">
      <Breadcrumbs items={[inicio, { label: 'Facturas' }]} meta={`${facturas.length} comprobante${facturas.length === 1 ? '' : 's'}`} />

      {/* ═══ KPI Cards ═══ */}
      <div className="pd-kpis" style={{ marginBottom: 20 }}>
        <div className={`pd-kpi ${totalPendiente > 0 ? 'pd-kpi--warning' : ''}`}>
          <div className="pd-kpi-icon" style={{ background: 'rgba(253,203,110,0.15)', color: '#fdcb6e' }}><IconDollar /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value" style={{ color: totalPendiente > 0 ? '#fdcb6e' : 'var(--text-secondary)' }}>
              {formatCurrency(totalPendiente, monedaCliente)}
            </span>
            <span className="pd-kpi-label">Pendiente de Pago</span>
          </div>
        </div>
        <div className="pd-kpi pd-kpi--success">
          <div className="pd-kpi-icon"><IconCheck /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{formatCurrency(totalPagado, monedaCliente)}</span>
            <span className="pd-kpi-label">Total Pagado</span>
          </div>
        </div>
        <div className="pd-kpi">
          <div className="pd-kpi-icon"><IconFile /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{facturas.length}</span>
            <span className="pd-kpi-label">Comprobantes</span>
          </div>
        </div>
      </div>

      {/* ═══ Chip Filters ═══ */}
      <div className="pt-filter-chips" style={{ marginBottom: 16 }}>
        {[
          { value: '', label: 'Todas' },
          { value: 'emitida', label: 'Pendientes' },
          { value: 'pagada', label: 'Pagadas' },
          { value: 'anulada', label: 'Anuladas' },
        ].map(opt => (
          <button
            key={opt.value}
            className={`pt-filter-chip ${filterEstado === opt.value ? 'active' : ''}`}
            onClick={() => setFilterEstado(opt.value)}
          >
            {opt.value && <span className="pt-filter-chip-dot" style={{ background: estadoConfig[opt.value]?.color || '#636e72' }} />}
            {opt.label}
          </button>
        ))}
        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem', marginLeft: 8 }}>
          {filtered.length} factura{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ═══ Invoice Table ═══ */}
      {filtered.length === 0 ? (
        <div className="pd-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <IconFile />
          <h3 style={{ marginTop: 12, color: 'var(--text-secondary)' }}>Sin facturas registradas</h3>
          <p style={{ color: 'var(--text-tertiary)' }}>No hay facturas registradas para tu cuenta.</p>
        </div>
      ) : (
        <div className="pf-table-wrap">
          <table className="pf-table">
            <thead>
              <tr>
                <th>Estado</th>
                <th>Detalle</th>
                <th>Emisión</th>
                <th>Vencimiento</th>
                <th style={{ textAlign: 'right' }}>Monto</th>
                <th style={{ textAlign: 'center' }}>Documento</th>
                <th style={{ textAlign: 'center', width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => {
                const effectiveEstado = (f.pago_confirmado_cliente && f.estado === 'emitida') ? 'pago_reportado' : f.estado
                const cfg = estadoConfig[effectiveEstado] ?? estadoConfig.emitida
                const overdue = isVencida(f)
                const isExpanded = expandedId === f.id
                const isPendingOrEmitida = f.estado === 'emitida' || f.estado === 'pre_factura'
                const fComps = comprobantes.filter(c => c.factura_id === f.id)

                return (
                  <React.Fragment key={f.id}>
                    <tr
                      className={`pf-table-row ${overdue ? 'pf-row-overdue' : ''} ${isExpanded ? 'pf-row-expanded' : ''}`}
                      onClick={() => setExpandedId(isExpanded ? null : f.id)}
                    >
                      <td>
                        <span className={`badge ${cfg.className}`}>{cfg.label}</span>
                        {overdue && <span className="pf-badge-vencida">Vencida</span>}
                      </td>

                      {/* El concepto sale de los ítems. `notas` NO: en una
                          factura son el cuaderno del equipo —a quién se le
                          mandó, el número de operación del voucher, qué falta
                          en el ERP— y acá las lee el cliente. */}
                      <td className="pf-cell-project">
                        {f.items?.[0]?.descripcion || '—'}
                      </td>
                      <td>{formatDate(f.fecha_emision)}</td>
                      <td>{f.fecha_vencimiento ? formatDate(f.fecha_vencimiento) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <strong>{formatCurrency(f.total, f.moneda)}</strong>
                      </td>
                      <td style={{ textAlign: 'center', position: 'relative' }}>
                        {(f.adjuntos && f.adjuntos.length > 0) || f.sunat_pdf_url ? (
                          <a
                            href={f.adjuntos?.[0]?.url || f.sunat_pdf_url!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pf-factura-link"
                            onClick={e => e.stopPropagation()}
                            title={f.adjuntos?.[0]?.nombre ? `Descargar ${f.adjuntos[0].nombre}` : 'Descargar factura SUNAT'}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            PDF
                          </a>
                        ) : (
                          // Sin comprobante SUNAT emitido todavía → acceso directo a la constancia
                          // (antes aquí salía un ícono con "PDF disponible próximamente", confuso
                          // porque la constancia SÍ está disponible; solo estaba oculta en el acordeón).
                          <button
                            className="pf-factura-link"
                            onClick={e => { e.stopPropagation(); setPreviewFactura(f) }}
                            title="Ver constancia de pago"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            Constancia
                          </button>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="pf-accordion-row">
                        <td colSpan={7}>
                          <div className="pf-accordion-cell open">
                            <div className="pf-accordion-inner">

                              {/* ── Items table (full width) ── */}
                              {f.items && f.items.length > 0 && (
                                <div className="pf-acc-items-block">
                                  <table className="pf-acc-items-table">
                                    <thead>
                                      <tr>
                                        <th>Concepto</th>
                                        <th style={{ textAlign: 'center', width: 60 }}>Cant.</th>
                                        <th style={{ textAlign: 'right', width: 120 }}>P. Unitario</th>
                                        <th style={{ textAlign: 'right', width: 120 }}>Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {f.items.map((item, idx) => (
                                        <tr key={idx}>
                                          <td>{item.descripcion}</td>
                                          <td style={{ textAlign: 'center' }}>{item.cantidad}</td>
                                          <td style={{ textAlign: 'right' }}>{formatCurrency(item.precio_unitario, f.moneda)}</td>
                                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.total, f.moneda)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {/* ── Financial summary strip ── */}
                              <div className="pf-acc-financial-strip">
                                <div className="pf-acc-strip-item">
                                  <span className="pf-acc-strip-label">Subtotal</span>
                                  <span className="pf-acc-strip-value">{formatCurrency(f.subtotal, f.moneda)}</span>
                                </div>
                                <div className="pf-acc-strip-divider" />
                                <div className="pf-acc-strip-item">
                                  <span className="pf-acc-strip-label">IGV ({f.igv_porcentaje}%)</span>
                                  <span className="pf-acc-strip-value">{formatCurrency(f.igv_monto, f.moneda)}</span>
                                </div>
                                <div className="pf-acc-strip-divider" />
                                <div className="pf-acc-strip-item pf-acc-strip-total">
                                  <span className="pf-acc-strip-label">Total</span>
                                  <span className="pf-acc-strip-value">{formatCurrency(f.total, f.moneda)}</span>
                                </div>
                                {f.moneda !== 'PEN' && f.tipo_cambio && (
                                  <>
                                    <div className="pf-acc-strip-divider" />
                                    <div className="pf-acc-strip-item">
                                      <span className="pf-acc-strip-label">PEN (TC {f.tipo_cambio.toFixed(3)})</span>
                                      <span className="pf-acc-strip-value">S/ {r2(f.total * f.tipo_cambio).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* ── Bottom: comprobantes + actions ── */}
                              <div className="pf-acc-bottom-row">
                                <div className="pf-acc-comprobantes-col">
                                  {uploading === f.id && (
                                    <div className="pf-upload-progress">
                                      <div className="pf-upload-progress-bar"><div className="pf-upload-progress-fill" /></div>
                                      <span className="pf-upload-text">Subiendo archivo...</span>
                                    </div>
                                  )}
                                  {fComps.length > 0 ? (
                                    <div className="pf-acc-files">
                                      {fComps.map(c => (
                                        <div key={c.id} className="pf-acc-file-row">
                                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="pf-acc-file" onClick={e => e.stopPropagation()}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                            <span>{c.nombre}</span>
                                            <span className="pf-acc-file-date">{formatDate(c.created_at.slice(0, 10))}</span>
                                          </a>
                                          <button className="pf-acc-file-delete" title="Eliminar" onClick={e => { e.stopPropagation(); handleDeleteComprobante(c) }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="pf-acc-empty-files">Sin comprobantes adjuntos</span>
                                  )}
                                </div>
                                <div className="pf-acc-actions">
                                  <button className="pf-acc-btn pf-acc-btn--preview" onClick={e => { e.stopPropagation(); setPreviewFactura(f) }} title="Ver documento">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                    Ver {infoDocumento(f).electronico ? infoDocumento(f).titulo.toLowerCase() : 'constancia'}
                                  </button>
                                  {f.cotizacion_id && (
                                    <a href={`/portal/cotizaciones?highlight=${f.cotizacion_id}`} className="pf-acc-btn pf-acc-btn--preview" onClick={e => e.stopPropagation()} style={{ textDecoration: 'none' }}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                      Cotización
                                    </a>
                                  )}
                                  {isPendingOrEmitida && (
                                    <>
                                      <button className="pf-acc-btn pf-acc-btn--pay" onClick={e => { e.stopPropagation(); setPayModalFactura(f) }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                                        Datos de Pago
                                      </button>
                                      <label className="pf-acc-btn pf-acc-btn--upload" onClick={e => e.stopPropagation()}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                        {uploading === f.id ? 'Subiendo...' : 'Subir Comprobante'}
                                        <input type="file" accept="image/*,.pdf" multiple style={{ display: 'none' }} disabled={uploading === f.id} onChange={ev => { const files = ev.target.files; if (files && files.length > 0) handleUploadComprobante(f.id, files); ev.target.value = '' }} />
                                      </label>
                                    </>
                                  )}
                                  {f.estado === 'emitida' && f.flow_url && (
                                    <a href={f.flow_url} target="_blank" rel="noopener noreferrer" className="pf-acc-btn pf-acc-btn--pay" onClick={e => e.stopPropagation()} style={{ background: 'linear-gradient(135deg, rgba(0,210,211,0.15), rgba(10,189,227,0.15))', color: '#00d2d3', borderColor: 'rgba(0,210,211,0.3)', textDecoration: 'none' }}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                                      Pagar Online
                                    </a>
                                  )}
                                </div>
                              </div>

                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Upload Confirm Modal ═══ */}
      {confirmModal && (
        <>
          <div className="pt-overlay" style={{ zIndex: 250 }} onClick={() => setConfirmModal(null)} />
          <div className="pf-confirm-modal" style={{ zIndex: 251 }}>
            <div className="pf-confirm-modal-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00b894" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h3 className="pf-confirm-modal-title">
              Comprobante{confirmModal.files.length > 1 ? 's' : ''} adjuntado{confirmModal.files.length > 1 ? 's' : ''}
            </h3>
            <div className="pf-confirm-modal-files">
              {confirmModal.files.map((name, i) => (
                <div key={i} className="pf-confirm-modal-file">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span>{name}</span>
                </div>
              ))}
            </div>
            <p className="pf-confirm-modal-text">
              ¿Desea confirmar que realizó el pago de esta factura?
            </p>
            <div className="pf-confirm-modal-actions">
              <button
                className="pf-acc-btn pf-acc-btn--preview"
                onClick={() => setConfirmModal(null)}
              >
                Solo Adjuntar
              </button>
              <button
                className="pf-acc-btn pf-acc-btn--confirm"
                onClick={() => handleConfirmPayment(confirmModal.facturaId)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                Confirmar Pago
              </button>
            </div>
            <p className="pf-confirm-modal-hint">
              Al confirmar, su pago quedará registrado para validación.
            </p>
          </div>
        </>
      )}

      {/* ═══ PDF Preview Modal ═══ */}
      {previewFactura && (
        <>
          <div className="pt-overlay" style={{ zIndex: 200 }} onClick={() => setPreviewFactura(null)} />
          <div className="pf-preview-center" style={{
            position: 'fixed', inset: 0, zIndex: 201, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', pointerEvents: 'none',
          }}>
            <div className="pf-preview-container" style={{ background: 'var(--bg-card)', borderRadius: 12, maxWidth: 860, width: '100%', maxHeight: '95vh', overflow: 'auto', position: 'relative', pointerEvents: 'auto' }}>
              {/* Modal header */}
              <div className="pf-preview-header" style={{
                position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-card)', padding: '16px 24px',
                borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <h3 className="pf-preview-title" style={{ margin: 0 }}>{infoDocumento(previewFactura).titulo} {infoDocumento(previewFactura).numero}</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-primary" onClick={exportPDF} disabled={exporting}>
                    {exporting ? 'Exportando...' : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{verticalAlign:'-2px',marginRight:4}}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Descargar PDF</>}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setPreviewFactura(null)}>✕ Cerrar</button>
                </div>
              </div>

              {/* PDF preview content */}
              <div ref={previewRef} className="pf-preview-paper" style={{ background: '#fff', color: 'var(--bg-card)', padding: 40 }}>
                {/* Header */}
                <div className="pf-preview-doc-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 30, borderBottom: '3px solid #1C4587', paddingBottom: 20 }}>
                  <div>
                    <img src="/assets/brand/logo-full-color.svg" alt="1Conecta" style={{ height: 36, marginBottom: 10 }} crossOrigin="anonymous" />
                    {empresa?.razon_social && <div style={{ fontSize: 12, color: '#666' }}>{empresa.razon_social}</div>}
                    {empresa?.ruc && <div style={{ fontSize: 12, color: '#666' }}>RUC: {empresa.ruc}</div>}
                    {empresa?.email && <div style={{ fontSize: 11, color: '#888' }}>{empresa.email}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: 22, color: '#1C4587' }}>{infoDocumento(previewFactura).titulo.toUpperCase()}</div>
                    <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'monospace' }}>N° {infoDocumento(previewFactura).numero}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Fecha: {formatDate(previewFactura.fecha_emision)}</div>
                    {previewFactura.fecha_vencimiento && (
                      <div style={{ fontSize: 12, color: '#666' }}>Vencimiento: {formatDate(previewFactura.fecha_vencimiento)}</div>
                    )}
                  </div>
                </div>

                {/* Client info */}
                <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#1C4587' }}>{infoDocumento(previewFactura).electronico ? 'ADQUIRIENTE:' : 'CLIENTE:'}</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{previewFactura.cliente_nombre}</div>
                  {previewFactura.cliente_ruc && <div style={{ fontSize: 13 }}>RUC: {previewFactura.cliente_ruc}</div>}
                  {previewFactura.proyecto_nombre && (
                    <div style={{ fontSize: 13, marginTop: 8, fontStyle: 'italic', color: '#555', borderTop: '1px solid #e0e0e0', paddingTop: 6 }}>
                      Proyecto: {previewFactura.proyecto_nombre}
                    </div>
                  )}
                </div>

                {/* Items table */}
                <table className="pf-preview-items" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
                  <thead>
                    <tr style={{ background: '#3468a8', color: '#fff' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>Descripción</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>P. Unit.</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, fontWeight: 600 }}>Cant.</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewFactura.items.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e9ecef', background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                        <td style={{ padding: '8px 12px', fontSize: 13 }}>{item.descripcion}</td>
                        <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right' }}>{formatCurrency(item.precio_unitario, previewFactura.moneda)}</td>
                        <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center' }}>{item.cantidad}</td>
                        <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.total, previewFactura.moneda)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                  <div className="pf-preview-totals" style={{ minWidth: 300 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ color: '#666' }}>Subtotal</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(previewFactura.subtotal, previewFactura.moneda)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ color: '#666' }}>IGV ({previewFactura.igv_porcentaje}%)</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(previewFactura.igv_monto, previewFactura.moneda)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700, fontSize: '1.2rem', borderTop: '2px solid #1C4587', marginTop: 4 }}>
                      <span>TOTAL</span>
                      <span style={{ color: '#1C4587' }}>{formatCurrency(previewFactura.total, previewFactura.moneda)}</span>
                    </div>
                    {previewFactura.moneda !== 'PEN' && previewFactura.tipo_cambio && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: '#666' }}>
                        <span>Total PEN (TC {previewFactura.tipo_cambio.toFixed(3)})</span>
                        <span>S/ {r2(previewFactura.total * previewFactura.tipo_cambio).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Naturaleza del documento — que no se confunda una constancia con un comprobante SUNAT */}
                {!infoDocumento(previewFactura).electronico ? (
                  <div style={{ background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 12, color: '#9A3412', lineHeight: 1.5 }}>
                    <strong>Constancia de pago.</strong> Es el registro interno de tu compra. <strong>No es un comprobante de pago electrónico autorizado por SUNAT</strong> (boleta o factura). Si necesitas tu comprobante electrónico, escríbenos por soporte.
                  </div>
                ) : infoDocumento(previewFactura).enlace ? (
                  <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                    <strong>Comprobante electrónico SUNAT.</strong> Consúltalo en{' '}
                    <a href={infoDocumento(previewFactura).enlace!} target="_blank" rel="noreferrer" style={{ color: '#166534', textDecoration: 'underline' }}>el portal de SUNAT</a>.
                  </div>
                ) : null}

                {/* Bank details */}
                {empresa && (empresa.banco || empresa.cuenta_corriente) && (
                  <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#1C4587' }}>DATOS BANCARIOS</div>
                    <table style={{ fontSize: 13 }}><tbody>
                      {empresa.banco && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>Banco:</td><td>{empresa.banco}</td></tr>}
                      {empresa.cuenta_corriente && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>Cuenta:</td><td style={{ fontFamily: 'monospace' }}>{empresa.cuenta_corriente}</td></tr>}
                      {empresa.cci && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>CCI:</td><td style={{ fontFamily: 'monospace' }}>{empresa.cci}</td></tr>}
                    </tbody></table>
                  </div>
                )}

              </div>
            </div>
          </div>
        </>
      )}
      {/* ═══ Pay Modal — Bank Deposit Info ═══ */}
      {payModalFactura && (
        <>
          <div className="pt-overlay" style={{ zIndex: 200 }} onClick={() => setPayModalFactura(null)} />
          <div style={{ position: 'fixed', inset: 0, zIndex: 201, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, pointerEvents: 'none' }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: 16, maxWidth: 480, width: '100%', pointerEvents: 'auto', overflow: 'hidden', border: '1px solid var(--border)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Datos de Pago</h3>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{infoDocumento(payModalFactura).titulo} {infoDocumento(payModalFactura).numero} - {formatCurrency(payModalFactura.total, payModalFactura.moneda)}</span>
                </div>
                <button className="pt-slideover-close" onClick={() => setPayModalFactura(null)}>✕</button>
              </div>
              <div style={{ padding: 24 }}>
                <div style={{ background: 'rgba(0,184,148,0.08)', border: '1px solid rgba(0,184,148,0.2)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,184,148,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00b894" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Depósito en Cuenta</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Transferencia bancaria directa</div>
                    </div>
                  </div>
                  {empresa && (
                    <table style={{ width: '100%', fontSize: '0.82rem' }}><tbody>
                      {empresa.banco && <tr><td style={{ padding: '4px 0', fontWeight: 600, width: 90 }}>Banco</td><td>{empresa.banco}</td></tr>}
                      {empresa.cuenta_corriente && <tr><td style={{ padding: '4px 0', fontWeight: 600 }}>Cuenta</td><td style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{empresa.cuenta_corriente}</td></tr>}
                      {empresa.cci && <tr><td style={{ padding: '4px 0', fontWeight: 600 }}>CCI</td><td style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{empresa.cci}</td></tr>}
                      {empresa.razon_social && <tr><td style={{ padding: '4px 0', fontWeight: 600 }}>Titular</td><td>{empresa.razon_social}</td></tr>}
                      {empresa.ruc && <tr><td style={{ padding: '4px 0', fontWeight: 600 }}>RUC</td><td>{empresa.ruc}</td></tr>}
                    </tbody></table>
                  )}
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textAlign: 'center', margin: 0 }}>
                  Luego de realizar el depósito, suba su comprobante con el botón "Confirmar Pago"
                </p>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  )
}
